import {
  ProgressEntry,
  RAUploadIncomingInvoiceProgress,
} from "@/apps/tatar/invoiceApp/components/UploadIncomingInvoice/RAUploadInvoiceContainer";
import InvoiceService from "@/apps/tatar/invoiceApp/InvoiceService";
import { Invoice } from "@/apps/tatar/invoiceApp/RAInterfaces";
import { DeclineReasonActionType } from "@/apps/tatar/invoiceConfigApp/RACInterfaces";
import AssetLoader from "@/components/AssetLoader/AssetLoader";
import Username from "@/components/AvatarComponent/Username";
import { FV } from "@/components/Form/Validation/FormValidators";
import ModalManager from "@/components/ModalComponent/ModalManager";
import Log from "@/debug/Log";
import i18n from "@/i18n";
import { AssetTypes } from "@/model/AssetTypes";
import BFDropzone from "@/modules/abstract-ui/dropzone/BFDropzone";
import BFCheckbox from "@/modules/abstract-ui/forms/checkbox/BFCheckbox";
import BFInput from "@/modules/abstract-ui/forms/input/BFInput";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import BFPortal from "@/modules/abstract-ui/general/Portal/BFPortal";
import EZGroup from "@/modules/ez-form/form-group/ez-form/EZGroup";
import DataBusDefaults from "@/services/DataBusDefaults";
import StringUtils from "@/utils/StringUtils";
import classNames from "classnames";
import { useState } from "react";
import { Field } from "react-final-form";
import { ActivityApplicationConstants } from "../../../ActivityHooks";
import { APActivity, APTodo } from "../../../ActivityInterfaces";
import ActivityService from "../../../ActivityService";
import "./APTodoFeature.scss";
import APTodoUploadInvoice from "./APTodoUploadInvoice";

const INITIAL_VALUE = {
  addTodo: "",
};
interface APTodoFeatureProps {
  activity: APActivity;
  constants: ActivityApplicationConstants;
}
const APTodoFeature = (props: APTodoFeatureProps) => {
  return (
    <div className={classNames("ap-todo-feature")}>
      {props.activity.data.todos?.map((todo, index) => (
        <APTodoEntry {...props} todo={todo} key={todo.id} />
      ))}
      <EZGroup
        value={INITIAL_VALUE}
        submitText={i18n.t("ra:Activity.TodoFeature.Submit", "Hinzufügen")}
        onSubmit={async (values, form) => {
          const todoText = values.addTodo;
          if (todoText) {
            await ActivityService.addTodo(
              props.constants.serviceUrl,
              props.activity._id,
              todoText
            );
            form.change("addTodo", "");
          }
        }}
      >
        {(renderProps) => (
          <div className={`add-todo-entry`}>
            <Field name="addTodo">
              {({ input, meta }) => (
                <BFInput
                  size="sm"
                  prefix="+"
                  {...input}
                  {...FV.getValidation(meta)}
                  placeholder={i18n.t(
                    "ra:Activity.TodoFeature.AddTodoPlaceholder",
                    "Aufgabe hinzufügen"
                  )}
                />
              )}
            </Field>
          </div>
        )}
      </EZGroup>
    </div>
  );
};

export default APTodoFeature;

const APTodoEntry = (
  props: {
    todo: APTodo;
  } & APTodoFeatureProps
) => {
  const [uploadModalStatus, setUploadModalStatus] = useState<
    "unitSelection" | "uploading"
  >(null);
  const [uploadState, setUploadState] = useState<ProgressEntry[]>(null);
  const [loading, setLoading] = useState(false);
  let displayame = props.todo.displayName;
  if (props.todo.type !== DeclineReasonActionType.MANUAL_TASK) {
    displayame = i18n.t(
      `ra:Invoice.DeclineReasonActionType.${props.todo.type}Todo`,
      props.todo.type
    );
  }

  const changeStatus = async (done: boolean) => {
    setLoading(true);
    try {
      await ActivityService.changeTodoStatus(
        props.constants.serviceUrl,
        props.activity._id,
        props.todo.id,
        done
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file: File, invoice: Invoice) => {
    try {
      setUploadState([{ file, progress: 0 }]);
      setUploadModalStatus("uploading");

      const newInvoice = await InvoiceService.uploadNewInvoice(
        invoice.data.direction,
        invoice.data.type,
        file,
        (progress) => {
          setUploadState((prev) => {
            const newProgress = [...prev];
            newProgress[0] = {
              file,
              progress,
            };
            return newProgress;
          });
        }
      );

      await InvoiceService.linkInvoiceWithFollowUp(
        newInvoice._id,
        invoice._id,
        props.todo.type,
        props.activity._id
      );

      ModalManager.show({
        noPadding: true,
        size: "fluid",
        content: (state, setState, close) => (
          <APTodoUploadInvoice
            activityId={props.activity._id}
            referenceInvoice={invoice}
            actionType={props.todo.type}
            invoice={newInvoice}
            onClose={close}
          />
        ),
      });
      return newInvoice;
    } catch (e) {
      DataBusDefaults.toast({
        type: "error",
        text: i18n.t(
          "ra:Invoice.InvoiceUpload.Error",
          "Fehler beim Hochladen der Rechnung"
        ),
      });
    } finally {
      setUploadState(null);
      setUploadModalStatus(null);
    }
  };

  const renderContent = (open) => (
    <div className={`todo-entry`} key={props.todo.id}>
      <div className={`checkbox-entry`}>
        <BFCheckbox
          noMinnHeight
          loading={loading}
          checked={props.todo.done}
          onChange={(_value, checked) => changeStatus(checked)}
        >
          {displayame}
        </BFCheckbox>
        {open && (
          <BFButton
            size="xs"
            noPadding
            inline
            className={`margin-left-10`}
            appearance="link"
            onClick={open}
          >
            {i18n.t("ra:Activity.TodoFeature.UploadFile", "Hochladen")}
          </BFButton>
        )}
      </div>
      {props.todo.changeUser && (
        <div className={`change-info`}>
          <Username id={props.todo.changeUser} />
          <div className={`date`}>
            {StringUtils.formatDate(props.todo.changeDate, "datetime")}
          </div>
        </div>
      )}
    </div>
  );

  if (
    props.todo.type === DeclineReasonActionType.NEED_CORRECTION ||
    props.todo.type === DeclineReasonActionType.NEED_STORNO
  ) {
    return (
      <>
        {!!uploadModalStatus && (
          <BFPortal>
            <RAUploadIncomingInvoiceProgress progress={uploadState} />
          </BFPortal>
        )}
        <AssetLoader
          assetType={AssetTypes.Invoicing.Invoice}
          id={props.activity.data.linkedAsset?.assetId}
          render={(invoice: Invoice) => (
            <BFDropzone
              onDrop={(accepted, declined) => {
                if (accepted.length === 1) {
                  uploadFile(accepted[0], invoice).then((invoice) => {
                    Log.info("uploaded invoice", invoice);
                  });
                }
              }}
              multipe={false}
              accept={{
                "application/pdf": [],
                "application/xml": [],
                "text/xml": [],
              }}
              render={(open) => renderContent(open)}
            />
          )}
        />
      </>
    );
  } else {
    return renderContent(null);
  }
};
